import { useCallback, useEffect, useState } from 'react';
import { accounts, ApiError, type Account, type AccountsView, type SetupLink } from '../lib/api';

/**
 * Accounts — staff sign-in management (admin only).
 *
 * The React equivalent of the old server-rendered `/admin/users` page, rebuilt so it sits in the
 * same frame as Campaigns / Clients / Agencies. There is no self-service signup (D10): an admin
 * adds the account here and a one-time setup link is shown to pass on; the new person sets their
 * own password through the reset flow, so no password is ever known to anyone else or emailed to an
 * unconfirmed address. Emails are not editable (the login identity and unique key) and accounts are
 * disabled, never deleted — the audit trail references their ids for years (R2).
 */
export function AccountsScreen() {
  const [view, setView] = useState<AccountsView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [link, setLink] = useState<SetupLink | null>(null);

  const refresh = useCallback(() => {
    accounts.list()
      .then((v) => { setView(v); setError(null); })
      .catch((e) => {
        // A reviewer who types the URL is refused by the admin-only guard (403) — explain, don't
        // bounce them to sign in (they are signed in).
        if (e instanceof ApiError && e.status === 403) setForbidden(true);
        else setError(e instanceof ApiError ? e.message : 'Could not load accounts.');
      });
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const run = useCallback(async (key: string, work: () => Promise<void>) => {
    setError(null); setBusy(key);
    try { await work(); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'That could not be completed.'); }
    finally { setBusy(null); }
  }, []);

  if (forbidden) {
    return (
      <section className="table-block wide">
        <header className="table-head"><h2>Accounts</h2></header>
        <div className="banner banner-warn" style={{ margin: '1rem' }}>
          Accounts management is restricted to administrators.
        </div>
      </section>
    );
  }

  return (
    <section className="table-block wide">
      <header className="table-head">
        <h2>Accounts</h2>
      </header>
      <p className="sub" style={{ margin: '0 1rem 0.25rem' }}>
        Staff sign-in for the reporting app. Reviewers may review, edit and approve; administrators
        also manage accounts.
      </p>

      {error && <div className="banner error" style={{ margin: '0.75rem 1rem' }}>{error}</div>}

      {link && (
        <div className="banner ok" style={{ margin: '0.75rem 1rem' }}>
          <strong>
            {link.reissued ? `Password link issued for ${link.email}.` : `Account created for ${link.email}.`}
          </strong>
          <p style={{ margin: '0.35rem 0' }}>
            {link.emailed
              ? 'Emailed to them. It works once and expires in an hour — the link is below too, in case the mail does not arrive.'
              : 'Send them this link to set their password. It works once and expires in an hour. It is shown here rather than emailed because the address is not yet confirmed by a sign-in.'}
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <code style={{ userSelect: 'all', wordBreak: 'break-all', flex: 1 }}>{link.url}</code>
            <button className="button" onClick={() => navigator.clipboard?.writeText(link.url)}>Copy</button>
            <button className="button" onClick={() => setLink(null)}>Dismiss</button>
          </div>
        </div>
      )}

      {view === null ? <div className="busy">Loading…</div> : (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Name</th><th>Email</th><th style={{ width: '9rem' }}>Role</th>
                <th style={{ width: '12rem' }}>Last signed in</th>
                <th style={{ width: '8rem' }}>Status</th>
                <th style={{ width: '1%' }} />
              </tr>
            </thead>
            <tbody>
              {view.accounts.map((u) => (
                <AccountRow
                  key={u.userId}
                  account={u}
                  isSelf={u.userId === view.me.userId}
                  busy={busy}
                  onSave={(name, role) => run(`save:${u.userId}`, async () => {
                    await accounts.update(u.userId, { name, role });
                    refresh();
                  })}
                  onResetLink={() => run(`link:${u.userId}`, async () => {
                    const { setupLink } = await accounts.resetLink(u.userId);
                    setLink(setupLink);
                    refresh();
                  })}
                  onToggleDisabled={() => run(`dis:${u.userId}`, async () => {
                    await accounts.setDisabled(u.userId, !u.disabled);
                    refresh();
                  })}
                />
              ))}
              {view.accounts.length === 0 && (
                <tr><td colSpan={6} className="empty">No accounts yet — add the first one below.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {view?.breakGlassEmail && (
        <p className="sub" style={{ margin: '0.75rem 1rem' }}>
          <strong>{view.breakGlassEmail}</strong> also signs in, from <code>ADMIN_EMAIL</code> and{' '}
          <code>ADMIN_PASSWORD</code> in the environment. It has no row here and cannot be edited or
          disabled from this screen — remove those variables and restart to withdraw it. It exists so
          the first account can be created and a lockout undone.
        </p>
      )}

      <AddAccount
        busy={busy === 'create'}
        onAdd={(input) => run('create', async () => {
          const { setupLink } = await accounts.create(input);
          setLink(setupLink);
          refresh();
        })}
      />
    </section>
  );
}

function AccountRow({ account, isSelf, busy, onSave, onResetLink, onToggleDisabled }: {
  account: Account;
  isSelf: boolean;
  busy: string | null;
  onSave: (name: string, role: 'admin' | 'reviewer') => void;
  onResetLink: () => void;
  onToggleDisabled: () => void;
}) {
  const [name, setName] = useState(account.name);
  const [role, setRole] = useState(account.role);

  // Re-sync the draft when the row reloads (e.g. after a save elsewhere).
  useEffect(() => { setName(account.name); setRole(account.role); }, [account.name, account.role]);

  const dirty = name.trim() !== account.name || role !== account.role;
  const anyBusy = busy !== null;

  return (
    <tr>
      <td style={{ whiteSpace: 'normal' }}>
        <input
          type="text" value={name} onChange={(e) => setName(e.target.value)}
          aria-label={`Name for ${account.email}`} style={{ width: '100%', font: 'inherit' }}
        />
      </td>
      <td>{account.email}</td>
      <td>
        <select
          value={role} onChange={(e) => setRole(e.target.value as 'admin' | 'reviewer')}
          aria-label={`Role for ${account.email}`}
          /* An admin cannot demote themselves — the server refuses it too. */
          disabled={isSelf}
          style={{ width: '100%', font: 'inherit' }}
        >
          <option value="reviewer">Reviewer</option>
          <option value="admin">Administrator</option>
        </select>
      </td>
      <td>{account.lastLoginAt ? new Date(account.lastLoginAt).toLocaleString('en-US') : <span className="sub">never</span>}</td>
      <td>
        <span className={account.disabled ? 'pill off' : 'pill ok'}>{account.disabled ? 'disabled' : 'active'}</span>
        {account.locked && <> <span className="pill done">locked</span></>}
      </td>
      <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
        <button className="button primary" disabled={!dirty || anyBusy}
          onClick={() => onSave(name.trim(), role)}>
          {busy === `save:${account.userId}` ? 'Saving…' : 'Save'}
        </button>{' '}
        <button className="button" disabled={anyBusy} onClick={onResetLink}>
          {busy === `link:${account.userId}` ? 'Working…' : 'Password link'}
        </button>{' '}
        {isSelf ? <span className="sub">you</span> : (
          <button className="button danger" disabled={anyBusy} onClick={onToggleDisabled}>
            {busy === `dis:${account.userId}` ? 'Working…' : account.disabled ? 'Re-enable' : 'Disable'}
          </button>
        )}
      </td>
    </tr>
  );
}

function AddAccount({ busy, onAdd }: {
  busy: boolean;
  onAdd: (input: { name: string; email: string; role: 'admin' | 'reviewer' }) => void;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'reviewer'>('reviewer');

  const submit = () => {
    if (!name.trim() || !email.trim()) return;
    onAdd({ name: name.trim(), email: email.trim(), role });
    setName(''); setEmail(''); setRole('reviewer');
  };

  return (
    <div style={{ margin: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--rule)' }}>
      <h3 style={{ margin: '0 0 0.6rem' }}>Add someone</h3>
      <div className="keys-add" style={{ flexWrap: 'wrap' }}>
        <input type="text" placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} />
        <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <select value={role} onChange={(e) => setRole(e.target.value as 'admin' | 'reviewer')}
          style={{ font: 'inherit', padding: '0.45rem 0.55rem' }}>
          <option value="reviewer">Reviewer — review, edit and approve</option>
          <option value="admin">Administrator — also manages accounts</option>
        </select>
        <button className="button primary" disabled={busy || !name.trim() || !email.trim()} onClick={submit}>
          {busy ? 'Creating…' : 'Create account'}
        </button>
      </div>
      <p className="sub" style={{ margin: '0.6rem 0 0' }}>
        D17: any admin may approve, including the preparer. The two roles separate account management
        from report work, not preparation from approval.
      </p>
    </div>
  );
}
